import { useLoader } from '@/context/Loader';
import { useMessage } from '@/context/Message';
import { useOidc } from '@/context/Oidc';
import { useLog } from '@/features/shared/Hooks/useLog';
import { useMobileSnackBarPosition } from '@/hooks/useMobileSnackBarPosition';
import { usePostMessageListener } from '@/hooks/usePostMessageListener';
import {
  EXPERLOGIX_MINUTES_EXPIRATION,
  EXPERLOGIX_SIGN_IN_URI,
  EXPERLOGIX_SIGN_OUT_URI,
} from '@/utils/constants';
import FramePopupUtils from '@/utils/FramePopupUtils';
import { getGenetecPostMessage } from '@/utils/helpers';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useCookies } from 'react-cookie';
import { EXPERLOGIX_EXPIRATION_TIME_KEY } from '../types';
import { usePowerAppsCookieKeys } from '../usePowerAppsCookieKeys';

const postMessageConnection = getGenetecPostMessage({ source: 'experlogix', type: 'connection' });

export const useExperlogixLoader = () => {
  const { forceLoad } = useLoader();
  const { error } = useMessage();
  const { messagePosition } = useMobileSnackBarPosition();
  const iframeRef = useRef<HTMLIFrameElement>();
  const [iframeLoading, setIframeLoading] = useState(false);
  const [iframeFailed, setIframeFailed] = useState(false);
  const [isSessionExpired, setIsSessionExpired] = useState(false);
  const { requireAuthentication } = useOidc();
  const [popupLoading, setPopupLoading] = useState(false);
  const [popupFailed, setPopupFailed] = useState(false);
  const loadMessageTriggered = useRef<boolean>(false);
  const onLoadMessage = useRef<() => void>(() => {});
  const Log = useLog({ context: 'useExperlogixLoader' });
  const waitingForConnection = useRef<((value: void | PromiseLike<void>) => void)[]>([]);
  const expirationTimeout = useRef<NodeJS.Timeout>();
  const isLoading = useMemo(() => iframeLoading || popupLoading, [iframeLoading, popupLoading]);
  const hasFailed = useMemo(() => iframeFailed && popupFailed, [iframeFailed, popupFailed]);

  const [EXPIRATION_TIME_KEY] = usePowerAppsCookieKeys([EXPERLOGIX_EXPIRATION_TIME_KEY]);

  const [{ [EXPIRATION_TIME_KEY]: experlogixExpirationTime }, setCookie, removeCookie] =
    useCookies<any>([EXPIRATION_TIME_KEY]);
  const isLoaded = useMemo(() => experlogixExpirationTime > 0, [experlogixExpirationTime]);

  const setExpirationTime = useCallback(() => {
    const expires = new Date(Date.now() + EXPERLOGIX_MINUTES_EXPIRATION * 60000);

    setCookie(EXPERLOGIX_EXPIRATION_TIME_KEY, expires.getTime(), {
      expires,
      path: '/',
    });

    setIsSessionExpired(false);
  }, [setCookie, setIsSessionExpired]);

  const setIsLoaded = useCallback(
    (value: boolean) => {
      if (value) {
        setIframeFailed(false);

        setExpirationTime();
      } else {
        removeCookie(EXPERLOGIX_EXPIRATION_TIME_KEY);
      }
    },
    [removeCookie, setExpirationTime],
  );

  useEffect(() => {
    if (isLoading) {
      forceLoad(true);
      return;
    }

    if (!isLoading) {
      forceLoad(false);
    }
  }, [forceLoad, isLoading]);

  const resolveWaitingForConnection = useCallback(() => {
    waitingForConnection.current.forEach((resolve) => {
      resolve();
    });

    waitingForConnection.current = [];
  }, []);

  const handleLoadedMessage = useCallback(
    async (props: any) => {
      const data = postMessageConnection(props);

      if (!data) return;

      if (!/loaded/.test(data?.event)) return;
      if (loadMessageTriggered.current) return;

      if (isLoaded && !isSessionExpired) {
        Log('"load message" triggered');

        onLoadMessage.current();
        return;
      }

      Log('"load message" triggered before loaded');
      loadMessageTriggered.current = true;
      onLoadMessage.current();
    },
    [Log, isLoaded, isSessionExpired, onLoadMessage],
  );

  const monitorLoadMessageTriggered = useMemo(
    () => (ms: number, callback: () => void) => {
      return setInterval(() => {
        if (loadMessageTriggered.current && callback) {
          callback();
        }
      }, ms);
    },
    [],
  );

  const retryWithPopup = useCallback(async () => {
    setPopupFailed(false);
    setPopupLoading(true);
    Log('retrying with popup');

    if (popupLoading) {
      return new Promise<void>((resolve) => {
        waitingForConnection.current.push(resolve);
      });
    }

    if (isLoaded && !isSessionExpired) {
      return Promise.resolve();
    }

    let failedTimer: NodeJS.Timeout;
    let loadedTimer: NodeJS.Timeout;

    // Open Popup window
    const popUpWindow = FramePopupUtils.openSizedPopup({
      urlNavigate: EXPERLOGIX_SIGN_IN_URI,
      popupName: 'useExperlogixLoader',
      height: 112,
      width: 426,
    });

    const handleOnLoadMessage = () => {
      // Popup Loaded
      setIsLoaded(true);
      setPopupLoading(false);
      Log('popup loaded');

      resolveWaitingForConnection();

      if (failedTimer) clearInterval(failedTimer);
      if (loadedTimer) clearInterval(loadedTimer);

      if (popUpWindow) {
        popUpWindow.close();
      }
    };

    onLoadMessage.current = handleOnLoadMessage;

    if (popUpWindow && !popUpWindow.closed) {
      loadedTimer = monitorLoadMessageTriggered(500, handleOnLoadMessage);
      failedTimer = setInterval(() => {
        if (popUpWindow.closed) {
          if (failedTimer) clearInterval(failedTimer);
          if (loadedTimer) clearInterval(loadedTimer);

          // Popup Closed
          error({
            title: 'Popup closed unexpectedly',
            message: 'Please click on "connect" to establish connection with CPQ.',
            duration: null,
            actionLabel: 'CONNECT',
            onAction: retryWithPopup,
            position: messagePosition,
          });
          Log('popup closed');
        }
      }, 500);

      return new Promise<void>((resolve) => {
        waitingForConnection.current.push(resolve);
      });
    }

    // Popup Blocked
    error({
      title: 'Popup blocked',
      message: 'Please click on "connect" to establish connection with CPQ.',
      duration: null,
      actionLabel: 'CONNECT',
      onAction: retryWithPopup,
      position: messagePosition,
    });
    Log('popup blocked');

    return Promise.resolve();
  }, [
    Log,
    monitorLoadMessageTriggered,
    isLoaded,
    setIsLoaded,
    error,
    resolveWaitingForConnection,
    popupLoading,
    messagePosition,
    isSessionExpired,
  ]);

  useEffect(() => {
    if (experlogixExpirationTime) {
      if (expirationTimeout.current) {
        clearTimeout(expirationTimeout.current);
      }

      expirationTimeout.current = setTimeout(
        () => {
          Log('Session Timeout');
          setIsSessionExpired(true);
        },
        Number(experlogixExpirationTime) - Date.now(),
      );
    }

    return () => {
      if (expirationTimeout.current) clearTimeout(expirationTimeout.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [experlogixExpirationTime]);

  // Initial Load with iframe
  const load = useCallback(async () => {
    if (isLoading) {
      await new Promise<void>((resolve) => {
        waitingForConnection.current.push(resolve);
      });
    }

    if ((isLoaded || iframeFailed) && !isSessionExpired) {
      return Promise.resolve();
    }

    const handleExpAuthorizeMessage = ({ data }: any) => {
      if (!/expAuthorize|expInit/.test(data)) return;

      if (/expAuthorize/.test(data)) {
        iframeRef.current?.remove();
        // Iframe Failed
        setIframeFailed(true);
        setIframeLoading(false);
        Log('iframe failed');

        retryWithPopup();
      }

      if (/expInit/.test(data)) {
        iframeRef.current?.remove();

        // Iframe Loaded
        setIsLoaded(true);
        setIframeLoading(false);
        Log('iframe loaded');
      }

      window.removeEventListener('message', handleExpAuthorizeMessage);
    };

    const handleFrameLoad = () => {
      const handleOnLoadMessage = () => {
        iframeRef.current?.remove();

        // Iframe Loaded
        setIsLoaded(true);
        setIframeLoading(false);
        Log('iframe loaded');

        // clearTimeout(failureTimer);
        window.removeEventListener('message', handleExpAuthorizeMessage);
      };

      if (loadMessageTriggered.current) {
        handleOnLoadMessage();
        return;
      }

      onLoadMessage.current = handleOnLoadMessage;
    };

    window.addEventListener('message', handleExpAuthorizeMessage);

    loadMessageTriggered.current = false;

    const hasQueryParams = EXPERLOGIX_SIGN_IN_URI.includes('?');
    iframeRef.current = document.createElement('iframe');
    iframeRef.current.src = `${EXPERLOGIX_SIGN_IN_URI}${
      hasQueryParams ? '&' : '?'
    }framed=1&auth_strategy=integrated`;
    iframeRef.current.onload = handleFrameLoad;
    iframeRef.current.style.visibility = 'hidden';
    iframeRef.current.style.position = 'absolute';
    iframeRef.current.style.display = 'none';
    document.body.appendChild(iframeRef.current);

    Log('loading iframe');
    setIframeLoading(true);
    setIframeFailed(false);

    return new Promise<void>((resolve) => {
      waitingForConnection.current.push(resolve);
    });
  }, [
    Log,
    setIsLoaded,
    setIframeLoading,
    isLoading,
    isLoaded,
    iframeFailed,
    isSessionExpired,
    retryWithPopup,
  ]);

  // Unload with iframe
  const unload = useCallback(() => {
    if (!isLoaded) return Promise.resolve();

    return new Promise<void>((resolve) => {
      const iframe = document.createElement('iframe');
      iframe.src = EXPERLOGIX_SIGN_OUT_URI;
      iframe.onload = () => {
        setIsLoaded(false);
        iframe.remove();
        Log('unloaded');
        resolve();
      };

      document.body.appendChild(iframe);

      Log('unloading');
    });
  }, [Log, isLoaded, setIsLoaded]);

  const refresh = useCallback(async () => {
    if (!isSessionExpired) return;

    await requireAuthentication();

    loadMessageTriggered.current = false;
    setIsLoaded(false);
    setIsSessionExpired(false);
  }, [setIsLoaded, isSessionExpired, setIsSessionExpired, requireAuthentication]);

  usePostMessageListener(handleLoadedMessage);

  return {
    load,
    refresh,
    unload,
    isLoading,
    isLoaded,
    hasFailed,
    iframeFailed,
    isSessionExpired,
    retryWithPopup,
  };
};
